import type { Styles } from '@adornis/ass/style.js';
import { l } from '@adornis/base/rxjs-extension/rxjs-helpers.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import '@adornis/buchungsheld-theme/components/tr-button-small';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { plainEditorConfig } from '@adornis/html-based-buildify/client/editor/presets/plain-editor.js';
import { richTextEditorConfig } from '@adornis/html-based-buildify/client/editor/presets/rich-text-editor.js';
import { listConfig } from '@adornis/html-based-buildify/client/nodes/list.js';
import { MenuItemGroup } from '@adornis/html-based-buildify/client/types.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { Language } from '@adornis/translation-core/languages.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, map, type Observable } from 'rxjs';

export type SupportedLangRecord = { [k in Language]?: string };

/**
 * @element bh-admin-multi-lang-text
 */
@customElement('bh-admin-multi-lang-text')
export class BHAdminMultiLangText extends FormField<TextMultipleLangItem[]> {
  @property({ attribute: false }) availableLangs = new RXController<SupportedLangRecord | null>(this, null);
  @property({ attribute: 'label', type: String }) label = new RXController<string | null>(this, null);
  @property({ attribute: 'no-formatting', type: Boolean }) noFormatting?: boolean;

  @property({ attribute: false }) defaultLang = new RXController(
    this,
    globalLanguageSubject.asObservable() as Observable<Language>,
  );

  @property({ attribute: false }) buildifyConfigs = {
    richText: true,
    listings: true,
  };

  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  protected _defaultLangs = Object.fromEntries(
    [Language.GERMAN, Language.ENGLISH].map(
      lV => [lV, this._translation.translate('PRODUCT', 'languages', lV) ?? lV] as [Language, Language],
    ),
  );

  protected _selectedLangKey = new RXController<Language | null>(this, null);
  protected _selectedTranslation = new RXController(
    this,
    combineLatest([this._selectedLangKey.observable, this.value.observable]).pipe(
      l('_selectedTranslation'),
      map(
        ([selKey, allTranslations]) =>
          (allTranslations ?? []).find(t => t.langKey === (selKey ?? Language.GERMAN)) ??
          new TextMultipleLangItem({
            langKey: selKey ?? Language.GERMAN,
            text: '',
          }),
      ),
      l('_selectedTranslation'),
    ),
  );

  protected get getAvailableLangs() {
    return (
      this.availableLangs.value ??
      // Default All Supported Langs as SupportedLangRecord Dic
      this._defaultLangs
    );
  }
  protected get getSelectedTranslation() {
    return this._selectedTranslation.value;
  }
  protected get getSelectedLangKey() {
    return this.getSelectedTranslation?.langKey ?? this.defaultLang.value ?? Language.GERMAN;
  }
  protected get getSelectedLangTitle() {
    return this.getAvailableLangs[this.getSelectedLangKey]!;
  }

  protected async _applyDescription(contentLang: Language, content: Maybe<string>) {
    const translations = this.value.value ?? [];
    const nextContent = content ?? '';
    const withoutTarget = translations.filter(t => t.langKey !== contentLang);

    const nextTranslations = withoutTarget.concat([
      new TextMultipleLangItem({
        langKey: contentLang,
        text: nextContent,
      }),
    ]);

    this.value.next(nextTranslations);
    this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: nextTranslations } }));
    this.requestUpdate();
  }

  protected _renderLangItem() {
    const targetTranslation = this.getSelectedTranslation;
    const langKey = this.getSelectedLangTitle;

    return html`
      <x-text ${css({ opacity: '.7', marginTop: '3px' })}>${langKey}</x-text>
      <x-editor
        .configs=${(() => {
          const items = [] as any[];
          if (this.buildifyConfigs.richText)
            items.push(
              ...richTextEditorConfig({
                colors: [this.colors.tone.error, this.colors.tone.warning, this.colors.tone.success],
              }),
            );
          else items.push(...plainEditorConfig(), listConfig());

          if (this.buildifyConfigs.listings) items.push(itemList());

          return items;
        })()}
        .value=${targetTranslation?.text ?? ''}
        .globalSettingsClassName="BuildifyProductDescriptionGlobalSettings"
        @value-picked=${evt => {
          this._applyDescription(targetTranslation?.langKey ?? Language.GERMAN, evt.detail.value);
        }}
      ></x-editor>
    `;
  }

  override render() {
    return html`
      <tr-flex wrapper space="sm">
        <tr-flex horizontal space="xs">
          ${this.label.value ? html` <tr-text tone="subtle"> ${this.label.value} </tr-text> ` : nothing}
          ${this.error ? html` <tr-text tone="error"> ${this.error} </tr-text> ` : nothing}
        </tr-flex>

        <tr-basic-card content>
          <tr-flex horizontal space="sm" padding="sm" flex>
            <tr-flex lang-choice space="xxs" crossaxis-center>
              <tr-icon ${css({ color: this.colors.accent.shade(600) })}>translate</tr-icon>
              <tr-flex space="xs">
                ${repeat(Object.entries(this.getAvailableLangs), ([curLang, curText]) => {
                  return html`
                    <tr-button-small
                      mode="${this.getSelectedLangKey === curLang ? 'fill' : 'outlined'}"
                      @click=${evt => {
                        this._selectedLangKey.next(curLang as Language);
                      }}
                    >
                      <tr-text> ${curLang.toUpperCase()} </tr-text>
                    </tr-button-small>
                  `;
                })}
              </tr-flex>
            </tr-flex>

            <tr-flex space="md" flex> ${this._renderLangItem()} </tr-flex>
          </tr-flex>
        </tr-basic-card>
      </tr-flex>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        '[wrapper]': {
          '[content]': {
            background: 'TRANSPARENT',
            borderRadius: this.spacing.sm,

            '[lang-choice]': {
              paddingRight: '8px',
              borderRight: `1px solid ${this.colors.accent}`,
            },

            'tr-button-small': {
              aspectRatio: '1',

              'tr-text': {
                textTransform: 'uppercase',
                fontWeight: 'bold',
              },
            },
          },
        },
      },
      {
        ':host': {
          '&[error]': {},
        },
      },
    ] as Styles[];
  }
}

function itemList() {
  const LIST_KEY = 'itemList2';

  return {
    mark: {
      name: LIST_KEY,
      schema: {},
    },
    menuItems: [
      {
        group: MenuItemGroup.TEXT_STYLE,
        label: 'Bullet List',
        icon: 'list',
        shouldVisualize: view => {
          return true;
        },
        run: async view => {
          const { state } = view;
          const { tr } = view.state;
          const nodeType = state.schema.nodes.list;

          const selectionFrom = view.state.selection.from;
          const resolvedPos = view.state.doc.resolve(selectionFrom);
          const currentNode = resolvedPos.parent; // parent gibt die Node an der Position zurück
          const listItemContent = state.schema.nodes.paragraph!.create({}, [state.schema.text('listitem 1')]);
          const listItem = nodeType.create({}, [listItemContent]);

          if (currentNode && currentNode.type === state.schema.nodes.paragraph && currentNode.textContent === '') {
            // Ersetze den leeren Paragraphen durch den neuen Node
            tr.replaceSelectionWith(listItem);
          } else {
            // Andernfalls füge den Node wie gewohnt ein
            tr.insert(selectionFrom, listItem);
          }

          view.dispatch(tr);
          return true;
        },
      },
    ],
    plugins: schema => [],
  };
}
